// Board-sized images the client composes itself (the light map, the fog's masks) as GL textures fed
// straight from their bytes. A CanvasTexture took a `putImageData`, then re-uploaded the whole canvas
// on `refresh()` and again on `setFilter()` (Phaser re-uploads the source to change a filter): four
// full uploads of a 568×368 image per light-map compose, 30 times a second, on a crawl for twenty.
// Here the filter is set once, and each update uploads only the rectangle that changed, from the
// same bytes the canvas held (every sample is opaque, so nothing is premultiplied either way).
// The texture keeps a view of the caller's bytes, so a lost WebGL context restores it as it stands.
import Phaser from 'phaser';

/** A rectangle in texels (end exclusive). */
export interface TexelRect { x0: number; y0: number; x1: number; y1: number }

export class DataTexture {
  private gl?: Phaser.Renderer.WebGL.Wrappers.WebGLTextureWrapper;
  private canvas?: Phaser.Textures.CanvasTexture;
  /** Rows of a part, packed for the upload (WebGL1 has no UNPACK_ROW_LENGTH). */
  private scratch = new Uint8Array(0);

  /**
   * A `w`×`h` RGBA texture under `key`, holding `data` (w·h·4 bytes, every alpha 255). `linear`
   * stretches it smoothly (the light map); otherwise NEAREST.
   */
  constructor(private readonly scene: Phaser.Scene, readonly key: string, readonly w: number, readonly h: number, data: Uint8ClampedArray, linear: boolean) {
    const r = scene.game.renderer;
    if (r.type === Phaser.WEBGL) {
      const wr = r as Phaser.Renderer.WebGL.WebGLRenderer, gl = wr.gl;
      const filter = linear ? gl.LINEAR : gl.NEAREST;
      this.gl = wr.createTexture2D(0, filter, filter, gl.CLAMP_TO_EDGE, gl.CLAMP_TO_EDGE, gl.RGBA, bytes(data), w, h, true, true, true);
      // the texture manager gives every new texture the game's pixel-art NEAREST: set ours once
      scene.textures.addGLTexture(key, this.gl)?.setFilter(linear ? Phaser.Textures.FilterMode.LINEAR : Phaser.Textures.FilterMode.NEAREST);
    } else {
      this.canvas = scene.textures.createCanvas(key, w, h)!;
      this.canvas.context.putImageData(new ImageData(new Uint8ClampedArray(data), w, h), 0, 0);
      this.canvas.refresh();
    }
  }

  /** Upload `data` (the same size), all of it or only `part`. */
  upload(data: Uint8ClampedArray, part?: TexelRect): void {
    const w = this.w, h = this.h;
    const x0 = Math.max(0, part?.x0 ?? 0), y0 = Math.max(0, part?.y0 ?? 0), x1 = Math.min(w, part?.x1 ?? w), y1 = Math.min(h, part?.y1 ?? h);
    if (x1 <= x0 || y1 <= y0) return;
    if (this.canvas) {
      this.canvas.context.putImageData(new ImageData(new Uint8ClampedArray(data), w, h), 0, 0, x0, y0, x1 - x0, y1 - y0);
      this.canvas.refresh();
      return;
    }
    const t = this.gl;
    if (!t || !t.webGLTexture) return;
    const wr = this.scene.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer, gl = wr.gl;
    if (gl.isContextLost()) return;
    // what a restored context uploads
    t.pixels = bytes(data);
    wr.glTextureUnits.bind(t, 0);
    // uploaded flipped, as Phaser uploads every canvas (its frames' UVs expect it): the part's rows
    // land bottom-up, so its GL y offset counts from the bottom
    wr.glWrapper.updateTexturing({ texturing: { flipY: true, premultiplyAlpha: true } });
    const dw = x1 - x0, dh = y1 - y0;
    let src: Uint8Array;
    if (dw === w) src = bytes(data).subarray(y0 * w * 4, y1 * w * 4);
    else {
      if (this.scratch.length < dw * dh * 4) this.scratch = new Uint8Array(dw * dh * 4);
      src = this.scratch.subarray(0, dw * dh * 4);
      for (let y = y0; y < y1; y++) src.set(data.subarray((y * w + x0) * 4, (y * w + x1) * 4), (y - y0) * dw * 4);
    }
    gl.texSubImage2D(gl.TEXTURE_2D, 0, x0, h - y1, dw, dh, gl.RGBA, gl.UNSIGNED_BYTE, src);
  }

  destroy(): void {
    if (this.scene.textures.exists(this.key)) this.scene.textures.remove(this.key);
    this.gl = undefined; this.canvas = undefined;
  }
}

const bytes = (d: Uint8ClampedArray): Uint8Array => new Uint8Array(d.buffer, d.byteOffset, d.length);
